import axios from 'axios';
import dayjs from 'dayjs';
import React from 'react';
import { type FieldErrors } from 'react-hook-form';

import { config } from 'utils/config/env';
import { Logger } from 'utils/logger';

/**
 * Formats a currency symbol based on the currency code.
 *
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR').
 * @param {boolean} [showParens=true] - Whether to wrap the symbol in parentheses if showOnlySymbol is false.
 * @param {boolean} [showOnlySymbol=false] - Whether to return only the symbol.
 * @returns {string} The formatted currency symbol or code.
 */
export function formatCurrencySymbol(
  currency: string,
  showParens = true,
  showOnlySymbol = false
) {
  if (!currency) return '';

  // Get the currency symbol using Intl.NumberFormat
  let symbol: string | null = null;
  try {
    symbol = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(0)
      .replace(/0/g, '')
      .trim();
  } catch (error) {
    symbol = null;
  }

  // For unknown currencies, just return the code
  if (!symbol) return currency;

  const showParensAndOnlySymbol = showParens && showOnlySymbol;
  const noParensAndOnlySymbol = !showParens && showOnlySymbol;
  const showSymbolAndCurrencyNoParens = !showParens && !showOnlySymbol;

  if (showParensAndOnlySymbol) {
    return `(${symbol})`;
  }

  if (noParensAndOnlySymbol) {
    return symbol;
  }

  if (showSymbolAndCurrencyNoParens) {
    return `${symbol} ${currency}`;
  }

  return `${symbol} (${currency})`;
}

interface FormatCurrencyOptions {
  value: any;
  returnHyphen?: boolean;
  useCompactNotation?: boolean;
  currency?: string;
  currencySign?: 'accounting' | 'standard';
  useTwoDigitCompactNotation?: boolean;
  useColor?: boolean;
  returnHyphenOnZero?: boolean;
}

/**
 * Formats a numeric value as a currency string.
 *
 * @param {FormatCurrencyOptions} options - Formatting options.
 * @param {any} options.value - The value to format.
 * @param {boolean} [options.returnHyphen=false] - If true, returns '-' for zero or empty values.
 * @param {boolean} [options.useCompactNotation=false] - If true, uses compact notation (e.g., 1M).
 * @param {string} [options.currency='USD'] - The currency code.
 * @param {'accounting' | 'standard'} [options.currencySign='standard'] - The currency sign display style.
 * @param {boolean} [options.useTwoDigitCompactNotation=false] - If true, uses 2 decimal places for compact notation.
 * @param {boolean} [options.useColor=false] - If true, returns a React node with color styling (green for positive, red for negative).
 * @param {boolean} [options.returnHyphenOnZero=false] - If true, returns '-' specifically when value is 0.
 * @returns {string | React.ReactNode} The formatted currency string or React node.
 */
export function formatCurrency(
  options: FormatCurrencyOptions
): string | React.ReactNode {
  // Extract options with defaults
  const {
    value,
    returnHyphen = false,
    useCompactNotation = false,
    currency = 'USD',
    currencySign = 'standard',
    useTwoDigitCompactNotation = false,
    useColor = false,
    returnHyphenOnZero = false,
  } = options;
  const isNumericString =
    typeof value === 'string' && /^-?\d*\.?\d+$/.test(value);
  const normalizedValue = isNumericString ? parseFloat(value) : value;

  if (returnHyphen && (!value || normalizedValue === 0)) {
    return '-';
  }

  if (returnHyphenOnZero && normalizedValue === 0) {
    return '-';
  }

  if (typeof normalizedValue !== 'number' || Number.isNaN(normalizedValue)) {
    return '-';
  }

  let formatOptions: Intl.NumberFormatOptions = {};

  // If no currency is provided, we'll format as a number without currency symbol
  if (!currency) {
    Logger.error(
      'Currency not provided to formatCurrency. Showing as plain number.'
    );
    // Format as decimal with appropriate fraction digits
    if (useCompactNotation && useTwoDigitCompactNotation) {
      formatOptions = {
        style: 'decimal',
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      };
    } else if (useCompactNotation) {
      formatOptions = {
        style: 'decimal',
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      };
    } else {
      formatOptions = {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
    }
  } else {
    // Format with currency
    const currencyToUse = currency;
    if (useCompactNotation && useTwoDigitCompactNotation) {
      formatOptions = {
        style: 'currency',
        currency: currencyToUse,
        currencySign,
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      };
    } else if (useCompactNotation) {
      formatOptions = {
        style: 'currency',
        currency: currencyToUse,
        currencySign,
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      };
    } else {
      formatOptions = {
        style: 'currency',
        currency: currencyToUse,
        currencySign,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
    }
  }

  const number = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = new Intl.NumberFormat('en-US', formatOptions).format(
    number
  );

  if (useColor) {
    let className = 'color-primary';
    if (number > 0) className = 'text-green-7';
    else if (number < 0) className = 'text-red-7';

    let result = formatted;
    if (number > 0) result = '+' + formatted;
    return <span className={className}>{result}</span>;
  }

  return formatted;
}

const logoClient = axios.create();

async function tryFetchLogo(query: string) {
  try {
    const apiUrl = `https://api.logo.dev/search?q=${encodeURIComponent(query)}`;

    const { data } = await logoClient.get(apiUrl, {
      headers: {
        Authorization: `Bearer: ${config.VITE_LOGO_API_KEY}`,
      },
    });

    // Check for error responses
    if (data?.error || data?.status >= 400) {
      return 'Error fetching logo';
    }

    return data?.[0]?.logo_url || 'No results found';
  } catch (error) {
    return 'Error fetching logo';
  }
}

/**
 * Fetches a company logo URL based on a search query.
 *
 * @param {string} query - The search query (e.g., company name).
 * @returns {Promise<string>} The logo URL or an error message/fallback.
 */
export async function fetchLogo(query: string): Promise<string> {
  if (!query) return 'No query provided';

  try {
    // First try with the exact company name
    let logoUrl = await tryFetchLogo(query);

    // If no results, try with a simplified version of the name
    if (logoUrl === 'No results found') {
      // Split by common company suffixes and take the first part
      const simplifiedQuery = query
        .split(/\s+(?:LLC|Ltd|Limited|Inc|Corp|Corporation|Group)/i)[0]
        .trim();

      // Only try again if we actually simplified something
      if (simplifiedQuery !== query) {
        logoUrl = await tryFetchLogo(simplifiedQuery);
      }
    }

    return logoUrl;
  } catch (error) {
    return 'Error fetching logo';
  }
}

/**
 * Converts a constant case string (e.g., "HELLO_WORLD") to title case (e.g., "Hello World").
 *
 * @param {string} constantCaseString - The string to convert.
 * @returns {string} The converted title case string.
 */
export function constantCaseToTitleCase(constantCaseString: string): string {
  if (!constantCaseString || typeof constantCaseString !== 'string') return '';

  return constantCaseString
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Safely formats a date using dayjs with validation to prevent Safari errors.
 * See: https://github.com/Affi-Official/accounting-finance-automation-frontend/pull/576
 *
 * @param date - The date string to format
 * @param format - The format string (default: 'MM-DD-YYYY')
 * @param fallback - The fallback value if date is invalid (default: '')
 * @returns Formatted date string or fallback value
 */
export function safeDateFormat(
  date: string | undefined | null,
  format: string = 'MM-DD-YYYY',
  fallback: string = ''
): string {
  if (!date || !dayjs(date).isValid()) return fallback;

  return dayjs(date).format(format);
}

/**
 * Builds a URL query string from an object of parameters.
 * Filters out undefined, null, and empty string values.
 * Handles arrays by appending multiple keys.
 *
 * @param {Record<string, any>} params - The parameters object.
 * @returns {string} The URL query string.
 */
export function buildQueryParams(params: Record<string, any>): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      value.forEach((v) => queryParams.append(key, String(v)));
    } else if (typeof value === 'object') {
      return;
    } else if (value !== undefined && value !== null) {
      queryParams.set(key, String(value));
    }
  });

  return queryParams.toString();
}
/**
 * Formats a number with its ordinal suffix (e.g., 1st, 2nd, 3rd, 4th).
 *
 * @param {number} number - The number to format.
 * @returns {string} The number with its ordinal suffix.
 */
export const formatNumberWithOrdinalSuffix = (number: number) => {
  const absN = Math.abs(number);
  const lastTwoDigits = absN % 100;
  const lastDigit = absN % 10;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${number}th`; // special cases: 11th, 12th, 13th
  }
  if (lastDigit === 1) return `${number}st`;
  if (lastDigit === 2) return `${number}nd`;
  if (lastDigit === 3) return `${number}rd`;
  return `${number}th`;
};

/**
 * Formats a number into compact notation (e.g., 1.5M, 250K).
 * Uses Intl.NumberFormat for proper localization.
 *
 * @param {number} value - The number to format.
 * @returns {string} The formatted compact number string.
 */
export const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

/**
 * Strips a leading "SO " prefix from document names.
 * The backend prefixes source document names with "SO " but the UI should
 * display the raw name in most contexts.
 */
export const stripSOPrefix = (name: string | null | undefined): string =>
  name?.replace(/^SO\s+/i, '') ?? '';

/**
 * helper to get the first error message from React Hook Form errors object.
 * Handles nested errors.
 *
 * @param {FieldErrors} errors - The errors object from React Hook Form.
 * @returns {string} The first error message found.
 */
export const getFirstErrorMessageReactHookForm = (
  errors: FieldErrors
): string => {
  const firstError = Object.values(errors)[0];
  if (!firstError) return '';
  if (typeof firstError === 'object' && !('message' in firstError)) {
    const nested = Object.values(
      firstError as Record<string, { message?: string }>
    )[0];
    return nested?.message || '';
  }
  return (firstError as { message?: string })?.message || '';
};
